/**
 * The campaign-type radio card used by the create-campaign picker, shared with the
 * external-campaign import modal (#6381: "same component as the one in create campaign").
 * Extracted verbatim from routes/_authenticated/new/_layout/campaign/index.tsx.
 */

import { Flag, MapPin, Repeat, Swords, Tag, TrendingUp, Users } from 'lucide-react';

import googleAdsLogo from '/google-ads.svg';
import metaLogo from '/meta.png';

export interface CampaignTypeMeta {
  label: string;
  description: string;
  icon: React.ElementType;
}

export type CampaignTypeRowId = 'prospecting' | 'hipo' | 'retargeting' | 'brand' | 'competitor' | 'icp' | 'location';

export const TYPE_META: Record<CampaignTypeRowId, CampaignTypeMeta> = {
  brand: {
    label: 'Brand Campaign',
    description: 'Capture high-intent traffic searching for your company name.',
    icon: Flag,
  },
  prospecting: {
    label: 'Prospecting Campaign',
    description: 'Reach new potential customers with keyword- or audience-targeted ads.',
    icon: Tag,
  },
  hipo: {
    label: 'HiPo Campaign',
    description: 'Scale your top-performing keywords into a focused, higher-budget campaign.',
    icon: TrendingUp,
  },
  competitor: {
    label: 'Competitor Campaign',
    description: 'Target competitor brand keywords to capture customers considering switching.',
    icon: Swords,
  },
  icp: {
    label: 'ICP Campaign',
    description: 'Reach your ideal customer profiles with precision-targeted ads.',
    icon: Users,
  },
  location: {
    label: 'Location Campaign',
    description: 'Target specific geographic areas to drive local traffic and conversions.',
    icon: MapPin,
  },
  retargeting: {
    label: 'Retargeting Campaign',
    description: 'Re-engage people who already interacted with your ads or visited your site.',
    icon: Repeat,
  },
};

const PLATFORM_LOGO: Record<'google' | 'meta', string> = {
  google: googleAdsLogo,
  meta: metaLogo,
};

export function CampaignTypeOption({
  meta,
  platform,
  isSelected,
  disabled = false,
  onSelect,
}: {
  meta: CampaignTypeMeta;
  /** Badge on the icon tile; omit to render without a platform badge (import modal). */
  platform?: 'google' | 'meta';
  isSelected: boolean;
  /** #5937: the row's platform is not connected — visible but unselectable. */
  disabled?: boolean;
  onSelect: () => void;
}) {
  const Icon = meta.icon;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      className={`w-full flex items-center gap-4 p-4 rounded-lg border-2 text-left transition-colors ${
        disabled
          ? 'border-gray-200 opacity-60 cursor-not-allowed'
          : isSelected
            ? 'border-[#290BD9] bg-[#290BD9]/10'
            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
      }`}
    >
      <div
        className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
          isSelected ? 'border-[#290BD9]' : 'border-gray-300 bg-white'
        }`}
      >
        {isSelected && <div className="w-3 h-3 rounded-full bg-[#290BD9]" />}
      </div>

      <div
        className={`relative flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
          isSelected ? 'bg-[#290BD9] text-white' : 'bg-gray-100 text-gray-500'
        }`}
      >
        <Icon className="h-5 w-5" />
        {platform && (
          <img
            src={PLATFORM_LOGO[platform]}
            alt=""
            className="absolute -bottom-1 -right-1 h-4 w-4 rounded-sm bg-white p-px shadow-sm object-contain"
          />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <span className={`font-medium ${isSelected ? 'text-[#290BD9]' : 'text-gray-900'}`}>{meta.label}</span>
        <div className="text-sm mt-1 text-gray-500">{meta.description}</div>
      </div>
    </button>
  );
}
